import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, type RouterHistory } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { isApiError } from './lib/api';
import { AuthProvider, useAuth } from './lib/auth';
import { RealtimeProvider, type SocketFactory } from './lib/realtime';
import { createAppRouter } from './router';
import { Logo } from './components/icons';

export function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 10_000,
        refetchOnWindowFocus: true,
        retry: (n, e) => !(isApiError(e) && e.status >= 400 && e.status < 500) && n < 2,
      },
      mutations: { retry: false },
    },
  });
}

function Routed({ queryClient, history }: { queryClient: QueryClient; history?: RouterHistory }) {
  const auth = useAuth();
  const [router] = useState(() => createAppRouter({ auth, queryClient }, history));
  useEffect(() => {
    router.update({ ...router.options, context: { auth, queryClient } });
    void router.invalidate();
    if (!auth.admin) queryClient.clear();
  }, [auth, router, queryClient]);
  if (auth.status === 'checking')
    return (
      <div className="loading-screen" aria-busy="true">
        <Logo size={48} />
      </div>
    );
  return <RouterProvider router={router} context={{ auth, queryClient }} />;
}

export function App({
  queryClient: givenClient,
  history,
  socketFactory,
  skipRestore,
}: {
  queryClient?: QueryClient;
  history?: RouterHistory;
  socketFactory?: SocketFactory;
  skipRestore?: boolean;
}) {
  const [queryClient] = useState(() => givenClient ?? makeQueryClient());
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider skipRestore={skipRestore}>
        <RealtimeProvider factory={socketFactory}>
          <Routed queryClient={queryClient} history={history} />
        </RealtimeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
